"use client"

/**
 * The single bottom-sheet primitive for the PWA — the canon's "Dock Sheet"
 * (`.tondocs/ui/SHEET_CANON.md` §2): a solid dark panel whose body scrolls
 * under a transparent floating footer holding one primary CTA.
 *
 * Nothing else may render sheet chrome. `components/ui/bottom-sheet.tsx` was
 * the second implementation of this same concept, with the header hand-rolled
 * at each of its 13 call sites; it was absorbed here on 2026-08-01 and deleted.
 * That is why the chrome below is a CLOSED union rather than a `customHeader`
 * slot — an open slot is what manufactured the drift in the first place.
 *
 * @see .tondocs/ui/SHEET_CANON.md            — canon (§3 invariants, §6 sizing, §7 z-scale)
 * @see .tondocs/ui/HANDOFF_SHEET_UNIFICATION_2026_08_01.md — this consolidation
 * @see .tondocs/ui/OVERLAY_FOOTER.md         — footer contract (transparent + fade)
 * @see .tondocs/ui/MOBILE_INPUTS.md          — `text-base` on every field inside
 */

import React, { useState, useEffect, useMemo } from "react"
import { useTranslations } from "next-intl"
import { createPortal } from "react-dom"
import { motion, AnimatePresence, type Transition } from "framer-motion"
import { ChevronLeft, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { unreachable } from "@/lib/type-helpers"
import {
  SHEET_BACKDROP,
  SHEET_HEADER_HANDLE,
  SHEET_PANEL_DARK,
  SHEET_Z_DEFAULT,
} from "@/lib/sheet-styles"
import { resolveLayoutDvh, useSheetLayoutHeight } from "@/hooks/use-sheet-viewport-anchoring"
import { useBodyScrollLock } from "@/hooks/use-body-scroll-lock"
import { usePreventSheetKeyboard } from "@/hooks/use-prevent-sheet-keyboard"

interface DockSheetBaseProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: React.ReactNode
  footer?: React.ReactNode
  zIndex?: number
  height?: string
  contentClassName?: string
  /** COMPACT tier — panel hugs content (max 90dvh). Do not pass `height="auto"` alone; flex body collapses. */
  contentSized?: boolean
  /** Child owns scroll (e.g. SearchablePickerList). Skips outer scroll + footer padding reserve. */
  scrollManagedByChild?: boolean
  /** Framer overrides for surfaces with their own motion language (e.g. the feed's shared-element easing). */
  backdropTransition?: Transition
  panelTransition?: Transition
}

/**
 * Which chrome the sheet paints. A closed union, not an escape hatch: the
 * fields of one variant are `never` on the others, so `chrome="none"` with a
 * `title` does not compile.
 *
 * - `standard`   — handle + close + title row (+ optional back, + `belowTitle`). The default.
 * - `handleOnly` — handle + close, no title. For sheets whose body opens with its own heading.
 * - `none`       — no chrome at all. ONLY for sheets whose `children` are shared with a desktop
 *                  `Dialog` branch and therefore already carry their own close control; adding
 *                  chrome here would render two close buttons.
 */
type DockSheetChrome =
  | {
      chrome?: "standard"
      title: string
      onBack?: () => void
      titleAlign?: "left" | "center"
      belowTitle?: React.ReactNode
    }
  | {
      chrome: "handleOnly"
      title?: never
      onBack?: never
      titleAlign?: never
      belowTitle?: never
    }
  | {
      chrome: "none"
      title?: never
      onBack?: never
      titleAlign?: never
      belowTitle?: never
    }

export type DockSheetProps = DockSheetBaseProps & DockSheetChrome

export function DockSheet(props: DockSheetProps) {
  const {
    open,
    onOpenChange,
    children,
    footer,
    zIndex = SHEET_Z_DEFAULT,
    height = "90dvh",
    contentClassName,
    contentSized = false,
    scrollManagedByChild = false,
    backdropTransition,
    panelTransition,
  } = props

  /* The chrome controls are the only strings this primitive renders, and they
     are assistive-only — which is exactly why they were still hardcoded
     English while every call site around them was translated. `common.close` /
     `common.back` exist in all nine message files. The desktop twin
     `DialogCard` reads the SAME two keys, so a screen reader hears the same
     word on both branches of a responsive shell. */
  const tCommon = useTranslations("common")

  const [mounted, setMounted] = useState(false)
  const layoutViewportHeight = useSheetLayoutHeight(open)
  useBodyScrollLock(open)
  usePreventSheetKeyboard(open)
  useEffect(() => setMounted(true), [])

  const isContentSized = contentSized || height === "auto"

  const resolvedHeight = useMemo(() => {
    if (isContentSized) return "auto"
    return resolveLayoutDvh(height, layoutViewportHeight)
  }, [height, isContentSized, layoutViewportHeight])

  /* A hug panel is capped at the STANDARD tier; a panel with an explicit height
     is capped at THAT height. Capping everything at 90dvh silently shrank the
     TALL tier (`height="95dvh"`) back to STANDARD — canon §6. */
  const resolvedMaxHeight = useMemo(
    () => (isContentSized ? resolveLayoutDvh("90dvh", layoutViewportHeight) : resolvedHeight),
    [isContentSized, resolvedHeight, layoutViewportHeight],
  )

  const resolvedBackdropTransition = backdropTransition ?? { duration: 0.2 }
  const resolvedPanelTransition =
    panelTransition ?? { y: { type: "tween", duration: 0.3, ease: [0.4, 0, 0.2, 1] } }

  if (!mounted) return null

  /* Handle row — the grabber, plus the close button parked over it. `right-3`
     matches the cart, which is the chrome reference (`HANDOFF_SHEET_UNIFICATION` §2).

     36px button / 20px icon is the CROSS-SURFACE canon (HANDOFF_DIALOG_CHROME
     §6 Q1): the desktop twin `DialogCard` paints the identical control, and 35
     of the 56 dialogs render both branches of one surface. It used to be
     32px/16px here — a divergence a user meets by resizing the window. */
  const handleRow = (
    <div className="flex items-center relative justify-center pt-3 pb-1 bg-sheet">
      <div className={SHEET_HEADER_HANDLE} />
      <button
        type="button"
        onClick={() => onOpenChange(false)}
        className="w-9 h-9 flex items-center justify-center rounded-full bg-muted hover:bg-sheet-handle active:bg-sheet-handle transition-colors absolute right-3 top-3"
        aria-label={tCommon("close")}
      >
        <X className="w-5 h-5 text-muted-foreground" strokeWidth={2.5} />
      </button>
    </div>
  )

  const renderChrome = () => {
    const kind = props.chrome ?? "standard"
    switch (kind) {
      case "none":
        return null

      case "handleOnly":
        return <div className="shrink-0 relative z-30 bg-sheet">{handleRow}</div>

      case "standard": {
        const { title, onBack, titleAlign = "center", belowTitle } = props
        return (
          <div className="shrink-0 relative z-30 bg-sheet">
            {handleRow}
            {/* Back mirrors the close: parked over the handle row (`left-3
                top-3` vs the X's `right-3 top-3`), NOT in flow in the title
                row — in flow it sat 12px lower than the X (handle-row height
                + title-row padding) and the two controls visibly misaligned
                (2f acceptance find). The title row below reserves its
                footprint with the same spacer `center` always used, so the
                title geometry is unchanged. */}
            {onBack ? (
              <button
                type="button"
                onClick={onBack}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-muted hover:bg-sheet-handle active:bg-sheet-handle transition-colors absolute left-3 top-3"
                aria-label={tCommon("back")}
              >
                <ChevronLeft className="w-5 h-5 text-muted-foreground" strokeWidth={2.5} />
              </button>
            ) : null}
            {/* The spacers are load-bearing, not filler: the close button is
                absolutely positioned in the handle row ABOVE, but it is taller
                than that row and hangs down over this one, reaching 48px in
                from the right (`right-3` + `w-9`). Without flex room a long
                title truncates UNDER it (scar: the order number in
                `order-hub-detail-sheet`).

                The trailing spacer is therefore required at BOTH alignments,
                not just `center`: `px-3 + w-9` = 48px reserves exactly the
                button, `px-4 + w-9` = 52px reserves it with 4px to spare. It
                used to be centre-only, which left every `titleAlign="left"`
                sheet with zero reserve — the title ran to 16px from the edge,
                straight under the button. The leading spacer is the `center`
                half of the same job: it mirrors the trailing one so the title
                centres on the FREE space, not on the container. */}
            <div
              className={cn(
                "flex items-center gap-3 pb-3 pt-1 bg-sheet",
                titleAlign === "left" ? "px-4" : "px-3",
              )}
            >
              {onBack || titleAlign === "center" ? (
                <div className="w-9 h-9 shrink-0 -ml-1" aria-hidden />
              ) : null}
              <h2
                className={cn(
                  "font-semibold text-lg text-sheet-foreground truncate flex-1 min-w-0",
                  titleAlign === "center" && "text-center",
                )}
              >
                {title}
              </h2>
              <div className="w-9 shrink-0" aria-hidden />
            </div>
            {belowTitle ? (
              <div className="shrink-0 px-4 pb-3 pt-1 bg-sheet">{belowTitle}</div>
            ) : null}
          </div>
        )
      }

      default:
        return unreachable(kind)
    }
  }

  const panel = (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={resolvedBackdropTransition}
            className={cn("fixed inset-0 md:hidden", SHEET_BACKDROP)}
            style={{ bottom: "-100vh", height: "calc(100vh + 100vh)", zIndex: zIndex - 10 }}
            onClick={() => onOpenChange(false)}
          />
          <div className="fixed left-0 right-0 bottom-0 md:hidden" style={{ zIndex }}>
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={resolvedPanelTransition}
              className={cn("relative flex flex-col", SHEET_PANEL_DARK)}
              style={{
                height: resolvedHeight,
                maxHeight: resolvedMaxHeight,
                paddingTop: "env(safe-area-inset-top)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {renderChrome()}
              {isContentSized && !footer ? (
                <div
                  className={cn("shrink-0 overflow-x-hidden px-4 pt-4 text-sheet-foreground", contentClassName)}
                  style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}
                >
                  {children}
                </div>
              ) : isContentSized ? (
                /* HUG + footer. The tall branch below cannot serve this: it
                   positions the body `absolute inset-0` inside a `flex-1`, which
                   collapses to zero height when the panel itself is `height:auto`.
                   So this branch keeps the body in FLOW and docks the footer after
                   it — the panel still shrinks to content, and only overflow past
                   `maxHeight` scrolls. */
                <div className="relative flex min-h-0 w-full flex-1 flex-col isolate">
                  <div className="relative flex min-h-0 flex-1 flex-col">
                    <div
                      className={cn(
                        "min-h-48 flex-1 overflow-y-auto overflow-x-hidden px-4 pt-4 text-sheet-foreground scrollbar-none [&::-webkit-scrollbar]:hidden",
                        contentClassName,
                      )}
                    >
                      {children}
                    </div>
                    <div
                      className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 h-20 bg-linear-to-t from-sheet to-transparent"
                      aria-hidden
                    />
                  </div>
                  <div
                    className="shrink-0 z-20 px-4 pt-4 text-sheet-foreground"
                    style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    {footer}
                  </div>
                </div>
              ) : (
                <div className="relative flex-1 min-h-0 z-0">
                  <div
                    className={cn(
                      "absolute inset-0 overflow-x-hidden px-4 pt-4 text-sheet-foreground",
                      scrollManagedByChild
                        ? "flex min-h-0 flex-col overflow-hidden"
                        : "overflow-y-auto scrollbar-none [&::-webkit-scrollbar]:hidden",
                      contentClassName,
                    )}
                    style={{
                      paddingBottom: footer
                        ? "calc(6rem + env(safe-area-inset-bottom))"
                        : scrollManagedByChild
                          ? "env(safe-area-inset-bottom)"
                          : "calc(1.5rem + env(safe-area-inset-bottom))",
                    }}
                  >
                    {children}
                  </div>
                  {footer ? (
                    <>
                      <div
                        className="absolute bottom-0 left-0 right-0 z-10 h-20 pointer-events-none bg-linear-to-t from-sheet to-transparent"
                        aria-hidden
                      />
                      <div
                        className="absolute bottom-0 left-0 right-0 z-20 px-4 pt-4 text-sheet-foreground"
                        style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
                        onClick={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        {footer}
                      </div>
                    </>
                  ) : null}
                </div>
              )}
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )

  return createPortal(panel, document.body)
}
